import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HintBar from '@/components/HintBar';

const HINTS = [
  'Each particle rides a rail matching its current 4B deployment',
  'Click any particle to cycle it to the next bucket',
  'Watch the Live Mix panel update as you re-route',
  'Recompose → when your mix looks right',
];

/* ================================================================
   Sort · 4B — Recipe 3.17 · Rail deployment matrix
   2-col: rail canvas (1fr) · mix panel (300)
   ================================================================ */

const BUCKET_ORDER = ['bot', 'borrow', 'build', 'bi', 'buy'];
const BUCKET_LABEL = { bot: 'Bot', borrow: 'Borrow', build: 'Build', bi: 'BI', buy: 'Buy' };

const STEPS = [
  { token: '✓',  label: 'Ingest',      path: '/analysis',    done: true },
  { token: '✓',  label: 'Deconstruct', path: '/deconstruct', done: true },
  { token: '✓',  label: 'Analyze',     path: '/analyze',     done: true },
  { token: '04', label: 'Sort · 4B',   path: '/sort',        active: true },
  { token: '05', label: 'Recompose',   path: '/recompose' },
];

function readBucketColors() {
  const cs = getComputedStyle(document.documentElement);
  const pick = (name, fallback) => (cs.getPropertyValue(name).trim() || fallback);
  return {
    bot:    pick('--bot',    '#00D4C8'),
    borrow: pick('--borrow', '#06B6D4'),
    build:  pick('--build',  '#C9A84C'),
    bi:     pick('--bi',     '#E8B96A'),
    buy:    pick('--buy',    '#EC4899'),
    text:   pick('--text',   '#FFFFFF'),
    t4:     pick('--t4',     'rgba(255,255,255,0.48)'),
    line:   pick('--line',   'rgba(255,255,255,0.10)'),
  };
}

function loadRole() {
  try {
    const stored = localStorage.getItem('reai_role');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.activities?.length) return parsed;
    }
  } catch { /* ignore */ }

  try {
    const raw = localStorage.getItem('analysis_mol_results');
    if (raw) {
      const mol = JSON.parse(raw);
      const r = (mol.roles || [])[0];
      if (r) {
        const tasks = r.task_breakdown || [];
        return {
          name: r.original_title || r.role_title || 'Role',
          function: localStorage.getItem('analysis_function') || '',
          hours: 40,
          activities: [
            {
              name: 'All tasks',
              hours: tasks.reduce((s, t) => s + (t.estimated_hours || 0), 0),
              subs: tasks.map((t) => ({
                name: t.task,
                hours: t.estimated_hours || 0,
                deployment: t.category_4b || 'build',
              })),
            },
          ],
        };
      }
    }
  } catch { /* ignore */ }

  return null;
}

const subIdOf = (ai, si) => `${ai}-${si}`;

function loadOverrides() {
  try {
    return JSON.parse(localStorage.getItem('reai_deploy') || '{}');
  } catch { return {}; }
}

function saveOverrides(map) {
  try {
    localStorage.setItem('reai_deploy', JSON.stringify(map));
  } catch { /* noop */ }
}

const EmptyState = ({ onGo }) => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, color: 'var(--text)', fontFamily: 'var(--fs)', position: 'relative', zIndex: 2 }}>
    <div className="eyebrow eye-teal">No role loaded</div>
    <p style={{ color: 'var(--t3)', fontSize: 14, maxWidth: 420, textAlign: 'center', lineHeight: 1.6 }}>
      Run Analysis first so sub-activities can be routed onto the 4B rails.
    </p>
    <button className="btn-teal" onClick={onGo}>Go to Analysis →</button>
  </div>
);

const Sort4B = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const rafRef = useRef(null);
  const hoverRef = useRef(null);

  const [overrides, setOverrides] = useState(loadOverrides());
  const [, forceRender] = useState(0); // cheap hover re-render

  // Global particles → ambient
  useEffect(() => {
    const api = window.__particles;
    if (!api) return;
    api.setMode('ambient');
    return () => api.setMode('ambient');
  }, []);

  const role = useMemo(() => loadRole(), []);

  const flatSubs = useMemo(() => {
    if (!role) return [];
    return (role.activities || []).flatMap((a, ai) =>
      (a.subs || []).map((s, si) => ({
        id: subIdOf(ai, si),
        name: s.name,
        hours: s.hours || 0,
        origDeployment: s.deployment || 'build',
        activityIdx: ai,
        subIdx: si,
        activityName: a.name,
      }))
    );
  }, [role]);

  const effectiveOf = (sub) => overrides[sub.id] || sub.origDeployment || 'build';

  const subsByBucket = useMemo(() => {
    const g = { bot: [], borrow: [], build: [], bi: [], buy: [] };
    flatSubs.forEach((s) => {
      const b = effectiveOf(s);
      if (g[b]) g[b].push(s);
      else g.build.push(s);
    });
    return g;
  }, [flatSubs, overrides]); // eslint-disable-line react-hooks/exhaustive-deps

  const cycleDeployment = (subId, currentBucket) => {
    // Functional setter so rapid multi-click doesn't clobber an in-flight
    // state update with a stale `overrides` snapshot from the previous render.
    setOverrides((prev) => {
      const effectiveCurrent = prev[subId] || currentBucket;
      const idx = BUCKET_ORDER.indexOf(effectiveCurrent);
      const next = BUCKET_ORDER[(idx + 1 + BUCKET_ORDER.length) % BUCKET_ORDER.length];
      const patch = { ...prev, [subId]: next };
      saveOverrides(patch);
      return patch;
    });
  };

  // ── Canvas rendering ────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    const stage = stageRef.current;
    if (!canvas || !stage) return;

    const ctx = canvas.getContext('2d');
    const colors = readBucketColors();
    let W = 0, H = 0, dpr = 1;

    const measure = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = stage.getBoundingClientRect();
      W = Math.max(400, rect.width);
      H = Math.max(300, rect.height);
      canvas.width = Math.floor(W * dpr);
      canvas.height = Math.floor(H * dpr);
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const LEFT_PAD = 96;
    const RIGHT_PAD = 110;
    const railYs = () => {
      const top = 50;
      const bottom = H - 50;
      const usable = bottom - top;
      return BUCKET_ORDER.map((_, i) => top + (i + 0.5) * (usable / BUCKET_ORDER.length));
    };

    const positions = () => {
      const ys = railYs();
      const out = [];
      BUCKET_ORDER.forEach((bucket, bi) => {
        const y = ys[bi];
        const subs = subsByBucket[bucket] || [];
        const color = colors[bucket];
        subs.forEach((s, si) => {
          const x = LEFT_PAD + ((si + 1) / (subs.length + 1)) * (W - LEFT_PAD - RIGHT_PAD);
          const r = Math.min(11, Math.max(5, Math.sqrt(Math.max(1, s.hours)) * 1.1));
          out.push({ s, x, y, r, color, bucket });
        });
      });
      return out;
    };

    const hitTest = (mx, my) => {
      const pts = positions();
      let best = null;
      let bestD2 = 22 * 22;
      for (const p of pts) {
        const dx = mx - p.x, dy = my - p.y;
        const d2 = dx * dx + dy * dy;
        const hitR = Math.max(p.r + 4, 14);
        if (d2 < hitR * hitR && d2 < bestD2) {
          bestD2 = d2;
          best = p;
        }
      }
      return best;
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const hit = hitTest(mx, my);
      const prev = hoverRef.current;
      const next = hit ? { id: hit.s.id, x: mx, y: my } : null;
      const changed = (prev?.id !== next?.id);
      hoverRef.current = next;
      canvas.style.cursor = hit ? 'pointer' : 'default';
      if (changed) forceRender((n) => n + 1);
    };

    const onLeave = () => {
      if (hoverRef.current) {
        hoverRef.current = null;
        forceRender((n) => n + 1);
      }
      canvas.style.cursor = 'default';
    };

    const onClick = (e) => {
      const rect = canvas.getBoundingClientRect();
      const hit = hitTest(e.clientX - rect.left, e.clientY - rect.top);
      if (hit) cycleDeployment(hit.s.id, hit.bucket);
    };

    const draw = (now) => {
      const t = now / 1000;
      ctx.clearRect(0, 0, W, H);

      const ys = railYs();

      // ── Rails
      BUCKET_ORDER.forEach((bucket, bi) => {
        const y = ys[bi];
        const color = colors[bucket];
        const subs = subsByBucket[bucket] || [];
        const hrs = subs.reduce((s, x) => s + (x.hours || 0), 0);

        ctx.globalAlpha = 0.25;
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.setLineDash([6, 3]);
        ctx.beginPath();
        ctx.moveTo(LEFT_PAD, y);
        ctx.lineTo(W - RIGHT_PAD, y);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;

        // Left label (bucket name)
        ctx.fillStyle = color;
        ctx.font = "600 11px 'Overpass Mono', monospace";
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillText(BUCKET_LABEL[bucket].toUpperCase(), LEFT_PAD - 14, y);

        // Rail endpoint dots
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.45;
        ctx.beginPath(); ctx.arc(LEFT_PAD, y, 3, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(W - RIGHT_PAD, y, 3, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;

        // Right label (count · hours)
        ctx.fillStyle = colors.t4;
        ctx.font = "9px 'Overpass Mono', monospace";
        ctx.textAlign = 'left';
        ctx.fillText(`${subs.length} · ${hrs}h`, W - RIGHT_PAD + 12, y);
      });

      // ── Particles
      const hover = hoverRef.current;
      const pts = positions();
      pts.forEach((p) => {
        const isHover = hover && hover.id === p.s.id;
        const pulse = isHover ? 1 + Math.sin(t * 6) * 0.25 : 1;
        const r = p.r * pulse;

        // Halo
        ctx.fillStyle = p.color + '33';
        ctx.beginPath();
        ctx.arc(p.x, p.y, r * 2, 0, Math.PI * 2);
        ctx.fill();

        // Dot
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();
      });

      // ── Hover tooltip (name · hours · current bucket)
      if (hover) {
        const p = pts.find((q) => q.s.id === hover.id);
        if (p) {
          const text = `${p.s.name.length > 34 ? p.s.name.slice(0, 34) + '…' : p.s.name}  ·  ${p.s.hours}h  ·  ${BUCKET_LABEL[p.bucket]}`;
          ctx.font = "11px 'Manrope', sans-serif";
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          const w = ctx.measureText(text).width + 16;
          // Horizontal clamp — flip to left if overflowing the right edge.
          let tx = p.x + 14;
          if (tx + w > W - 8) tx = Math.max(8, p.x - 14 - w);
          // Vertical clamp — flip below the particle if we'd clip the top edge.
          let ty = p.y - 20;
          if (ty - 10 < 4) ty = p.y + 24;
          if (ty + 12 > H - 4) ty = p.y - 20;
          ctx.fillStyle = 'rgba(10,10,26,0.9)';
          ctx.fillRect(tx - 4, ty - 10, w, 22);
          ctx.strokeStyle = p.color;
          ctx.lineWidth = 1;
          ctx.strokeRect(tx - 4, ty - 10, w, 22);
          ctx.fillStyle = colors.text;
          ctx.fillText(text, tx + 4, ty + 1);
        }
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(() => measure());
    });
    ro.observe(stage);
    measure();
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    canvas.addEventListener('click', onClick);
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('click', onClick);
    };
  }, [subsByBucket]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReset = () => {
    localStorage.removeItem('reai_role');
    localStorage.removeItem('reai_deploy');
    setOverrides({});
    navigate('/analysis');
  };

  if (!role) return <EmptyState onGo={() => navigate('/analysis')} />;

  const totalHours = flatSubs.reduce((s, x) => s + (x.hours || 0), 0);
  const totalSubs = flatSubs.length;
  const bucketStats = BUCKET_ORDER.map((b) => {
    const subs = subsByBucket[b] || [];
    const hrs = subs.reduce((s, x) => s + (x.hours || 0), 0);
    return { bucket: b, count: subs.length, hours: hrs };
  });
  const reroutedCount = flatSubs.filter((s) => overrides[s.id] && overrides[s.id] !== s.origDeployment).length;

  return (
    <div style={{
      height: '100vh',
      overflow: 'hidden',
      color: 'var(--text)',
      fontFamily: 'var(--fs)',
      position: 'relative',
      zIndex: 2,
      paddingTop: 'var(--nav-h)',
    }}>
      {/* ── Top nav ────────────────────────────────────── */}
      <header className="nav">
        <span className="nav-brand">re<span className="ai">AI</span>magination</span>
        <span className="nav-sep" />
        <span className="nav-lbl">Product flow</span>
        <nav className="nav-pages" style={{ marginLeft: 'auto' }}>
          <button onClick={() => navigate('/signal-room')}     className="np">Signal Room</button>
          <button onClick={() => navigate('/intelligence')}    className="np">Intelligence</button>
          <button onClick={() => navigate('/blueprint/cockpit')} className="np">Blueprint</button>
          <button onClick={() => navigate('/stage')}           className="np">Stage</button>
          <button onClick={() => navigate('/showcase/ai-tools')} className="np">Showcase</button>
        </nav>
      </header>

      {/* ── Chrome bar · 5-step stepper + role chip ───── */}
      <div className="chro-bar">
        <span className="cb-brand">re<span className="ai">AI</span>magination</span>
        <div className="cb-tabs">
          {STEPS.map((s) => (
            <button
              key={s.path}
              className={`ct${s.active ? ' on' : ''}`}
              onClick={() => !s.active && navigate(s.path)}
              style={{ opacity: s.done || s.active ? 1 : 0.6 }}
            >
              <span style={{ marginRight: 6, color: s.done ? 'var(--teal)' : 'inherit' }}>{s.token}</span>
              {s.label}
            </button>
          ))}
        </div>
        <div className="cb-right">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--t2)' }}>
            <span className="act-dot" style={{ background: 'var(--teal)', boxShadow: '0 0 5px var(--teal)' }} />
            {role.name}
          </span>
          <button
            onClick={handleReset}
            style={{
              padding: '4px 10px', fontSize: 10, fontFamily: 'var(--fm)',
              color: 'var(--t3)', background: 'transparent',
              border: '1px solid var(--line)', borderRadius: 'var(--r-sm)',
              cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.12em',
            }}
          >
            Reset
          </button>
        </div>
      </div>

      <HintBar screenId="sort" hints={HINTS} />

      {/* ── 2-col sort grid ──────────────────────────── */}
      <div
        className="sort-main"
        style={{
          height: 'calc(100vh - var(--nav-h) - var(--chro-h) - var(--hint-h, 0px) - var(--status-h))',
          overflow: 'hidden',
        }}
      >
        {/* Rails canvas */}
        <div
          className="sort-stage"
          ref={stageRef}
          style={{
            position: 'relative',
            overflow: 'hidden',
            minHeight: 0,
            height: '100%',
            background: 'rgba(10,10,26,0.08)',
          }}
        >
          <canvas
            id="sort-cv"
            ref={canvasRef}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              display: 'block',
            }}
          />
        </div>

        {/* Mix panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto', paddingRight: 4 }}>
          <div className="eyebrow eye-teal">Live mix</div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {bucketStats.map((b) => (
              <div className="mix-tile" key={b.bucket}>
                <div className="mt-label">
                  <span className="mt-dot" style={{ background: `var(--${b.bucket})` }} />
                  {BUCKET_LABEL[b.bucket]}
                </div>
                <div className="mt-val">{b.hours}h</div>
                <div className="mt-sub">{b.count} sub{b.count === 1 ? '' : 's'}</div>
              </div>
            ))}
          </div>

          {/* Stack bar */}
          <div className="stack-bar">
            {bucketStats
              .filter((b) => b.hours > 0)
              .map((b) => (
                <span key={b.bucket} style={{ flex: b.hours, background: `var(--${b.bucket})` }} />
              ))}
          </div>

          {/* Total hours */}
          <div style={{ marginTop: 4 }}>
            <div className="eyebrow">Total hours reclaimed</div>
            <div className="gap-num" style={{ fontSize: 32, color: 'var(--teal)', marginTop: 4 }}>
              {totalHours}h
            </div>
            <div className="eyebrow" style={{ marginTop: 4 }}>
              {reroutedCount > 0 ? `${reroutedCount} re-routed` : 'No overrides yet'}
            </div>
          </div>

          <button
            className="btn-teal"
            onClick={() => navigate('/recompose')}
            style={{ marginTop: 'auto' }}
          >
            Recompose →
          </button>
        </div>
      </div>

      {/* ── Status bar ───────────────────────────────── */}
      <div className="status-bar">
        <span className="st-live">● Live</span>
        <span className="st-sep">·</span>
        <span>session</span>
        <span className="st-sep">·</span>
        <span>sort</span>
        <span className="st-sep">·</span>
        <span>rails</span>
        <span className="st-sep">·</span>
        <span>particles · {totalSubs}</span>
      </div>
    </div>
  );
};

export default Sort4B;
